<template>
  <a-flex vertical gap="middle">
    <CombineProductsBySizeHeader />

    <a-table
      :columns="tableColumns"
      :data-source="tableData"
      :loading="isDataLoading"
      :scroll="{ x: 'max-content' }"
      :pagination="false"
      :sticky="{ offsetHeader: 65 }"
      :row-key="(item: CombineProductsBySizeTableItem) => item.id"
      :row-selection="
        isDataLoading
          ? {}
          : {
              selectedRowKeys: selectedRowKeys,
              onChange: onRowSelectionChange,
            }
      "
    >
      <template
        #headerCell="{
          column,
        }: {
          column: {
            dataIndex: keyof CombineProductsBySizeTableItem;
            title: string;
          };
        }"
      >
        <a-flex justify="center" v-if="column.dataIndex === 'category'">
          <span>
            {{ column.title }}
            <a-popover placement="bottom" trigger="click">
              <template #content>
                <a-flex class="categories" vertical gap="small">
                  <a-button
                    class="w-100"
                    :type="
                      combineProductsBySizeStore.filters.categoryId === item.id
                        ? 'primary'
                        : 'text'
                    "
                    v-for="item in customCategories"
                    :key="item.id"
                    @click="selectCategory(item.id)"
                  >
                    {{ item.name }}
                  </a-button>
                </a-flex>
              </template>
              <a-tooltip
                trigger=""
                title="Здесь вы можете сменить категорию"
                placement="bottom"
                :open="isCategoryTooltipShow"
              >
                <a-button
                  type="text"
                  :icon="h(FilterOutlined)"
                  @click="isCategoryFilterClicked = true"
                ></a-button>
              </a-tooltip>
            </a-popover>
          </span>
        </a-flex>
      </template>

      <template
        #bodyCell="{
          column,
          record,
        }: {
          column: { dataIndex: keyof CombineProductsBySizeTableItem };
          record: CombineProductsBySizeTableItem;
        }"
      >
        <BaskitImage v-if="column.dataIndex === 'image'" :url="record.image" />

        <CombineProductsBySizeLinksCell
          v-else-if="column.dataIndex === 'links'"
          :links="record.links"
        />

        <CombineProductsBySizeActionsCell
          v-else-if="column.dataIndex === 'actions'"
          :record="record"
        />

        <span v-else class="default-cell">{{ record[column.dataIndex] }}</span>
      </template>
    </a-table>

    <a-pagination
      style="margin-top: 20px; display: flex; justify-content: end"
      v-model:current="combineProductsBySizeStore.pagination.current"
      v-model:pageSize="combineProductsBySizeStore.pagination.pageSize"
      :showQuickJumper="combineProductsBySizeStore.pagination.showQuickJumper"
      :showSizeChanger="combineProductsBySizeStore.pagination.showSizeChanger"
      :showTotal="combineProductsBySizeStore.pagination.showTotal"
      :total="combineProductsBySizeStore.pagination.total"
      @change="combineProductsBySizeStore.pagination.onChangePagination"
    >
      <template #buildOptionText="props">
        <span>{{ props.value }} / странице</span>
      </template>
    </a-pagination>
  </a-flex>

  <full-size-page :isAction="true">
    <a-button type="primary" @click="combine"> Объединить </a-button>
  </full-size-page>
</template>

<script setup lang="ts">
import FullSizePage from "@/components/base/FullSizePage.vue";
import {
  type CombineProductsBySizeTableItem,
  useCombineProductsBySize,
} from "@/stores/product/combineProducts/combineProductsBySize/combine-products-by-size.store";
import { computed, h, onMounted, ref, watch } from "vue";
import CombineProductsBySizeHeader from "@/components/product/combineProducts/combineProductsBySize/CombineProductsBySizeHeader.vue";
import type { BaseProductDto } from "@/types/product/productTypes";
import BaskitImage from "@/components/UI/BaskitImage.vue";
import CombineProductsBySizeLinksCell from "@/components/product/combineProducts/combineProductsBySize/CombineProductsBySizeLinksCell.vue";
import CombineProductsBySizeActionsCell from "@/components/product/combineProducts/combineProductsBySize/CombineProductsBySizeActionsCell.vue";
import { createNotification } from "@/tools/notification";
import { FilterOutlined } from "@ant-design/icons-vue";
import { useCustomCategoriesStore } from "@/stores/customCategories/customCategories";
import type { CustomCategoryDto } from "@/api/classes/customCategories/customCategories.dto";
import type { ProductsForConnectinSizesFilter } from "@/api/classes/productsForConnectinSizes/products-for-connectin-sizes.api";
import { useRoute, useRouter } from "vue-router";

const combineProductsBySizeStore = useCombineProductsBySize();
const customCategoryStore = useCustomCategoriesStore();
const router = useRouter();
const route = useRoute();

const isCategoryFilterClicked = ref<boolean>(false);

const tableData = computed(
  () => combineProductsBySizeStore.productsTableData || [],
);
const tableColumns = computed(
  () => combineProductsBySizeStore.columnsFiltered || [],
);
const isCategoryTooltipShow = computed(
  () =>
    !tableData.value.length &&
    !isCategoryFilterClicked.value &&
    !isDataLoading.value,
);
const isDataLoading = computed(() => !combineProductsBySizeStore.isLoadingDone);
const selectedRowKeys = computed(
  () => combineProductsBySizeStore.selectedRowKeys,
);

const onRowSelectionChange = (selectedRowKeys: BaseProductDto["id"][]) => {
  combineProductsBySizeStore.updateSelectedItems(selectedRowKeys);
};

const selectCategory = (categoryId: CustomCategoryDto["id"]) => {
  combineProductsBySizeStore.filters.categoryId = categoryId;
};

const combine = () => {
  createNotification("warning", "Функционал в разработке");
  combineProductsBySizeStore.updateSelectedItems([]);
};

const customCategories = computed(() =>
  customCategoryStore.customCategories.filter((category) => category.sizesOn),
);

onMounted(async () => {
  await customCategoryStore.getCustomCategories();

  let customCategoryId;

  if (customCategories.value.length) {
    customCategoryId = combineProductsBySizeStore.filters.categoryId =
      customCategories.value[0].id;
  }

  const newFilter: Partial<ProductsForConnectinSizesFilter> = {};

  Object.entries(route.query as Record<string, string>).forEach(
    ([key, value]: [string, string]) => {
      const filter = filterKeys.find((i) => i.field === key);

      if (!filter || value === undefined) {
        return;
      }

      if (filter.type === "boolean") {
        newFilter[key] = value === "true";
      } else if (filter.type === "string") {
        newFilter[key] = Array.isArray(value) ? value.join(",") : value;
      }
    },
  );

  if (route.query.categoryId) {
    const category = customCategories.value.find(
      (category) => category.id === route.query.categoryId,
    );

    if (category) {
      customCategoryId = category.id;
    }
  }

  combineProductsBySizeStore.filters = {
    ...combineProductsBySizeStore.filters,
    ...newFilter,
    categoryId: customCategoryId,
  };
});

const filterKeys = [
  { field: "searchPhrase", type: "string" },
  { field: "createdInOzon", type: "boolean" },
  { field: "createdInWb", type: "boolean" },
  { field: "outOfStockFBS", type: "boolean" },
  { field: "outOfStockFBO", type: "boolean" },
  { field: "readyToExport", type: "boolean" },
  { field: "categoryId", type: "string" },
];

const updateQuery = () => {
  const query: Record<string, string> = {};

  filterKeys.forEach(({ field }) => {
    const value = combineProductsBySizeStore.filters[field];

    if (value !== undefined && value !== null && value !== "") {
      query[field] = String(value);
    }
  });

  router.push({ query });
};

watch(
  () => combineProductsBySizeStore.filters,
  () => updateQuery(),
  { deep: true },
);
</script>

<style scoped lang="scss">
.ant-pagination {
  margin-bottom: 70px;
}

.default-cell {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.categories {
  max-height: 200px;
  overflow: auto;
}

.w-100 {
  width: 100%;
}
</style>
